import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePSOwner, usePSUser } from '../../hooks'
import { CalendarFilterType } from '../../models/CalendarFilter'
import { Industry } from '../../models/Industry'
import { Item } from '../../models/Item'
import { User } from '../../models/User'
import {
    AvailabilityFilter,
    AvailabilitySearchOptions,
    AvailabiltySearchParams,
    TimeSlot,
    getAvailabilitySearchParams,
    getDurationFromDurationValue,
    getSkipToDateForView,
} from '../../utils/calendar'
import { getDurationInMinutes } from '../../utils/dates'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { CalendarAvailabilityContainer } from './CalendarAvailabilityContainer'
import { CalendarAvailabilityMiniCalendar } from './CalendarAvailabilityMinCalendar'
import { CalendarAvailabilityFooter } from './CalendarAvailabillityFooter'
import { CalendarScheduleDateLabel } from './CalendarScheduleDateLabel'
import { CalendarSelectCards } from './CalendarSelectCards'

type Props = {
    currentDate: moment.Moment
    availabilityFilter: AvailabilityFilter
    items: Item[]
    pros: User[]
}

export const CalendarAvailability = ({ currentDate, availabilityFilter, items, pros }: Props) => {
    const me = usePSUser()
    const owner = usePSOwner()

    const [selectedDay, setSelectedDay] = useState<moment.Moment | null>(currentDate.clone())
    const [selectedMonth, setSelectedMonth] = useState(moment().startOf('month'))
    const [selectedDaySearchParams, setSelectedDaySearchParams] = useState<AvailabiltySearchParams>(
        {}
    )
    const [selectedMonthSearchParams, setSelectedMonthSearchParams] =
        useState<AvailabiltySearchParams>({})
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null)
    const [showSelectProModal, setShowSelectProModal] = useState(false)
    const [industry, setIndustry] = useState<Industry | undefined>(me.industry)

    const selectedItem: Item | null | undefined = useMemo(
        () =>
            availabilityFilter.services.length > 0
                ? items.find((i) => !!i.id && i.id === availabilityFilter.services[0])
                : null,
        [availabilityFilter.services, items]
    )

    const setCalendarDate = (date: moment.Moment) => {
        console.log(`[todo] setCalendarDate ${date}`)
    }

    // TODO: consider classes with start dates as well
    useEffect(() => {
        if (selectedItem?.type !== 'reservation') return
        setSelectedDay(selectedItem?.startDate ? moment(selectedItem.startDate) : null)
    }, [selectedItem])

    useEffect(() => {
        if (!selectedDay) return
        setSelectedMonth(selectedDay.clone().startOf('month'))
        setCalendarDate(selectedDay)
    }, [selectedDay])

    useEffect(() => {
        clearScheduleSelections()
    }, [selectedDay, availabilityFilter])

    useEffect(() => {
        if (!selectedDay || !owner?.id || !industry) return

        const options: AvailabilitySearchOptions = {
            date: selectedDay.clone().startOf('month'),
            filter: availabilityFilter,
            items,
            industry,
            vendor: owner.id,
        }

        setSelectedDaySearchParams(getAvailabilitySearchParams(options))
        setSelectedMonthSearchParams(
            getAvailabilitySearchParams({ ...options, date: selectedMonth.clone() })
        )
    }, [owner, industry, selectedDay, selectedMonth, availabilityFilter, items])

    const [selectedProIds, setSelectedProIds] = useState<string[]>([])

    const isTeamAccount = useMemo(() => me?.type === 'company', [me?.type])

    const clearScheduleSelections = () => {
        setSelectedTimeSlot(null)
        setSelectedProIds([])
    }

    const setAvailabilityFilter = (filter: AvailabilityFilter) => {
        console.log('[todo] setAvailabilityFilter', filter)
    }

    const handleSelectDate = useCallback(
        (date: moment.Moment) => {
            if (
                !!selectedDay &&
                selectedItem?.type === 'reservation' &&
                !availabilityFilter.duration.value
            ) {
                const start = moment(date).isBefore(selectedDay) ? date : selectedDay
                const end = moment(date).isBefore(selectedDay) ? selectedDay : date

                if (end.isSame(selectedDay)) setSelectedDay(start)

                setAvailabilityFilter({
                    ...availabilityFilter,
                    duration: getDurationFromDurationValue(getDurationInMinutes(start, end)),
                })
            }
            else setSelectedDay(date)
        },
        [selectedDay, selectedItem, availabilityFilter]
    )

    const handleSelectTimeSlot = useCallback(
        (slot: TimeSlot) => {
            setSelectedTimeSlot(slot)

            if (isTeamAccount) {
                setSelectedProIds([])
                setShowSelectProModal(true)
            }
        },
        [isTeamAccount]
    )

    return (
        <>
            <div className="calendarAvailability mt-6 flex h-[calc(100%-68px)] items-start gap-6">
                <CalendarAvailabilityMiniCalendar
                    searchParams={selectedMonthSearchParams}
                    selectedDay={selectedDay}
                    selectedMonth={selectedMonth}
                    selectedItem={selectedItem}
                    availabilityByHash={{}}
                    availabilityFilter={availabilityFilter}
                    onSelectDate={(date) => handleSelectDate(date)}
                    onGoToToday={() => setSelectedMonth(moment().startOf('month'))}
                    onSkipMonth={(direction) =>
                        setSelectedMonth((lastMonth) =>
                            getSkipToDateForView(lastMonth, 'month', direction === 'left' ? -1 : 1)
                        )
                    }
                />

                <CalendarAvailabilityContainer
                    style={{ height: `calc(100% - ${!selectedTimeSlot ? '32px' : '108px'})` }}
                    searchParams={selectedDaySearchParams}
                    selectedDay={selectedDay}
                    selectedTime={selectedTimeSlot?.time || ''}
                    selectedItem={selectedItem}
                    selectedProIds={selectedProIds}
                    pros={pros}
                    availabilityByHash={{}}
                    availabilityFilter={availabilityFilter}
                    onSelectTimeSlot={(slot) => handleSelectTimeSlot(slot)}
                    onSelectEmployees={() => setShowSelectProModal(true)}
                />
            </div>

            <Modal
                title="Select Pros"
                isOpen={showSelectProModal}
                onClose={() => setShowSelectProModal(false)}
                footer={
                    <div className="flex w-full gap-4">
                        {!!selectedDay && !!selectedTimeSlot && (
                            <div className="mr-auto">
                                <CalendarScheduleDateLabel
                                    date={selectedDay}
                                    start={selectedTimeSlot.start}
                                    duration={selectedTimeSlot.duration}
                                    selectedItem={selectedItem}
                                />
                            </div>
                        )}
                        <Button onClick={() => setShowSelectProModal(false)}>Cancel</Button>
                        <Button intent="primary" onClick={() => setShowSelectProModal(true)}>
                            Done
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <CalendarSelectCards
                        type={CalendarFilterType.Pros}
                        list={pros}
                        selected={selectedProIds}
                        onSelect={setSelectedProIds}
                    />
                </div>
            </Modal>

            {selectedDay && selectedTimeSlot && (
                <CalendarAvailabilityFooter
                    selectedDay={selectedDay}
                    selectedTimeSlot={selectedTimeSlot}
                    selectedProIds={selectedProIds}
                    selectedItem={selectedItem}
                    pros={pros}
                    onEditPros={() => setShowSelectProModal(true)}
                    onDelete={clearScheduleSelections}
                />
            )}
        </>
    )
}
